import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';

export interface NotificationItem {
  id: number;
  senderUserId: number;
  title: string;
  body: string;
  sendEmail: boolean;
  /**
   * De qué va el aviso. `null` = lo escribió una persona; `'session'` = lo
   * emitió el sistema al desplazarse la sesión a otro dispositivo.
   *
   * Es lo que permite que un aviso salga en la campanita y no en el banner.
   * Puede faltar si el backend es anterior a la migración 076.
   */
  category?: string | null;
  createdAt: string;
  updatedAt: string;
  sender?: {
    id: number;
    email: string;
    firstname?: string;
    lastname?: string;
  };
}

export interface InboxItem {
  id: number;
  notificationId: number;
  userId: number;
  readAt: string | null;
  readSource: 'inapp' | 'push' | null;
  pushDeliveredAt: string | null;
  emailDeliveredAt: string | null;
  createdAt: string;
  notification: NotificationItem;
}

export interface CandidateUser {
  id: number;
  email: string;
  firstname?: string;
  lastname?: string;
  companyId: number;
}

interface InboxEnvelope {
  data: { items: InboxItem[]; total: number; page: number; limit: number };
}

interface UnreadEnvelope {
  data: { items: InboxItem[]; total: number };
}

interface CandidatesEnvelope {
  data: { items: CandidateUser[]; total: number };
}

export const notificationsApi = {
  listInbox: (params?: { unreadOnly?: boolean; page?: number; limit?: number }) =>
    api.get<InboxEnvelope>('/notifications/inbox', { params }),
  listUnread: () => api.get<UnreadEnvelope>('/notifications/inbox/unread'),
  listSent: (params?: { page?: number; limit?: number }) =>
    api.get<{ data: { items: NotificationItem[]; total: number } }>('/notifications/sent', { params }),
  listCandidates: () => api.get<CandidatesEnvelope>('/notifications/candidates'),
  compose: (body: { title: string; body: string; recipientUserIds: number[]; sendEmail?: boolean }) =>
    api.post<NotificationItem>('/notifications', body),
  markRead: (id: number, source: 'inapp' | 'push' = 'inapp') =>
    api.post<InboxItem>(`/notifications/${id}/read`, { source }),
  registerDevice: (body: { token: string; platform?: 'web' | 'android' | 'ios' }) =>
    api.post<{ id: number }>('/notifications/devices', body),
  removeDevice: (id: number) => api.delete(`/notifications/devices/${id}`),
};

export function useUnreadNotifications() {
  return useQuery({
    queryKey: queryKeys.notifications.unread,
    queryFn: async () => (await notificationsApi.listUnread()).data.items,
    staleTime: 15_000,
    refetchInterval: 60_000,
  });
}

export function useInbox(params?: { unreadOnly?: boolean; page?: number; limit?: number }) {
  return useQuery({
    queryKey: [...queryKeys.notifications.inbox, params ?? {}] as const,
    queryFn: async () => (await notificationsApi.listInbox(params)).data,
    staleTime: 15_000,
  });
}

export function useNotificationCandidates() {
  return useQuery({
    queryKey: queryKeys.notifications.candidates,
    queryFn: async () => (await notificationsApi.listCandidates()).data.items,
    staleTime: 60_000,
  });
}

export function useCompose() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: notificationsApi.compose,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, source }: { id: number; source?: 'inapp' | 'push' }) =>
      notificationsApi.markRead(id, source ?? 'inapp'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

export function useRegisterDevice() {
  return useMutation({
    mutationFn: notificationsApi.registerDevice,
  });
}
